const API_URL =
  import.meta.env.VITE_API_URL || "https://api-staging.wooflit.com";

const TIMEOUT_MS = 10000;

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem("token");
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      throw new ApiError(
        "Invalid response from server",
        "PARSE_ERROR",
        response.status,
      );
    }

    if (!response.ok) {
      // Handle different error response formats
      const errorData = data as {
        error?: string | { issues?: Array<{ message: string; path?: string[] }> };
        code?: string;
        success?: boolean;
      };

      let errorMessage = "Request failed";
      let errorCode = errorData.code || "UNKNOWN";

      if (typeof errorData.error === "string") {
        // Standard API error format: { error: "message", code: "CODE" }
        errorMessage = errorData.error;
      } else if (errorData.error && typeof errorData.error === "object") {
        // Zod validation error format from @hono/zod-validator
        // Format: { success: false, error: { issues: [...] } }
        const zodError = errorData.error as { issues?: Array<{ message: string; path?: string[] }> };
        const firstIssue = zodError.issues?.[0];
        if (firstIssue) {
          // Extract the first validation error message
          const path = firstIssue.path?.join(".") || "";
          errorMessage = path
            ? `${path}: ${firstIssue.message}`
            : firstIssue.message;
          errorCode = "VALIDATION_ERROR";
        }
      }

      throw new ApiError(errorMessage, errorCode, response.status);
    }

    return data as T;
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }
    if (err instanceof Error && err.name === "AbortError") {
      throw new ApiError("Request timed out", "TIMEOUT", 0);
    }
    throw new ApiError("Network error", "NETWORK_ERROR", 0);
  } finally {
    clearTimeout(timeout);
  }
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string;
}

interface AuthResponse {
  token: string;
  user: User;
}

interface MeResponse extends User {
  tenantName: string;
}

// Client types from API
export interface Client {
  id: string;
  tenantId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  } | null;
  timezone: string | null;
  notes: string | null;
  tags: string[] | null;
  isBanned: boolean;
  banReason: string | null;
  lastAppointmentAt: string | null;
  totalAppointments: number;
  totalSpent: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClientInput {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  } | null;
  timezone?: string | null;
  notes?: string | null;
  tags?: string[] | null;
  isBanned?: boolean;
  banReason?: string | null;
}

export type UpdateClientInput = Partial<CreateClientInput>;

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Dog types from API
export interface Dog {
  id: string;
  clientId: string;
  tenantId: string;
  name: string;
  breed: string | null;
  color: string | null;
  weight: string | null;
  dateOfBirth: string | null;
  gender: "male" | "female" | "unknown" | null;
  isNeutered: boolean | null;
  microchipId: string | null;
  notes: string | null;
  medicalNotes: string | null;
  behavioralNotes: string | null;
  vaccinations: Array<{
    name: string;
    date: string;
    expiresAt?: string;
  }> | null;
  profilePhotoUrl: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDogInput {
  clientId: string;
  name: string;
  breed?: string | null;
  color?: string | null;
  weight?: string | null;
  dateOfBirth?: string | null;
  gender?: "male" | "female" | "unknown" | null;
  isNeutered?: boolean | null;
  notes?: string | null;
}

export type UpdateDogInput = Partial<Omit<CreateDogInput, "clientId">>;

// Calendar types from API
export interface Calendar {
  id: string;
  tenantId: string;
  name: string;
  calendarType: "staff" | "resource";
  description: string | null;
  color: string;
  timezone: string;
  isActive: boolean;
  settings: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCalendarInput {
  name: string;
  calendarType?: "staff" | "resource";
  description?: string | null;
  color?: string;
  timezone?: string;
  isActive?: boolean;
  settings?: Record<string, unknown> | null;
}

export type UpdateCalendarInput = Partial<CreateCalendarInput>;

// Service types from API
export interface Service {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  duration: number;
  price: string;
  color: string | null;
  category: string | null;
  requiresDog: boolean;
  maxDogsPerAppointment: number;
  isActive: boolean;
  sortOrder: number;
  // Program/class fields
  bufferBeforeMinutes: number | null;
  bufferAfterMinutes: number | null;
  depositAmount: string | null;
  depositType: "fixed" | "percentage" | null;
  isPublic: boolean;
  isProgram: boolean;
  isGroupClass: boolean;
  maxParticipants: number | null;
  calendarIds: string[] | null;
  formIds: string[] | null;
  resourceIds: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServiceInput {
  name: string;
  description?: string | null;
  duration: number;
  price: string;
  color?: string | null;
  category?: string | null;
  requiresDog?: boolean;
  maxDogsPerAppointment?: number;
  isActive?: boolean;
  sortOrder?: number;
  // Program/class fields
  bufferBeforeMinutes?: number | null;
  bufferAfterMinutes?: number | null;
  depositAmount?: string | null;
  depositType?: "fixed" | "percentage" | null;
  isPublic?: boolean;
  isProgram?: boolean;
  isGroupClass?: boolean;
  maxParticipants?: number | null;
  calendarIds?: string[] | null;
  formIds?: string[] | null;
  resourceIds?: string[] | null;
}

export type UpdateServiceInput = Partial<CreateServiceInput>;

// Appointment types from API
export type AppointmentStatus =
  | "scheduled"
  | "confirmed"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "no_show";

export type LocationType = "facility" | "client_home" | "saved_location" | "virtual";

// Nested data from GET /appointments/:id (rich response)
export interface AppointmentDogData {
  name: string | null;
  breed: string | null;
  behavioralNotes: string | null;
  commandsLearned: string[] | null;
}

export interface AppointmentClientData {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  } | null;
}

export interface AppointmentServiceData {
  name: string | null;
  isProgram: boolean | null;
  duration: number | null;
  price: string | null;
}

export interface AppointmentSavedLocationData {
  name: string | null;
  googleMapsUrl: string | null;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    lat?: number;
    lng?: number;
  } | null;
}

export interface AppointmentCalendarData {
  name: string | null;
}

export interface AppointmentEnrollmentData {
  id: string;
  appointmentsCompleted: number;
  status: string;
}

export interface Appointment {
  id: string;
  tenantId: string;
  calendarId: string;
  clientId: string;
  dogId: string | null;
  appointmentTypeId: string | null;
  status: AppointmentStatus;
  startTime: string;
  endTime: string;
  price: string | null;
  notes: string | null;
  cancellationReason: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  completedAt: string | null;
  locationType?: LocationType;
  savedLocationId?: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined fields from list endpoint
  clientFirstName?: string | null;
  clientLastName?: string | null;
  clientPhone?: string | null;
  serviceName?: string | null;
  serviceColor?: string | null;
  dogName?: string | null;
  dogBreed?: string | null;
  calendarName?: string | null;
  calendarColor?: string | null;
  savedLocationName?: string | null;
  // Rich nested data from GET /:id endpoint
  dog?: AppointmentDogData | null;
  client?: AppointmentClientData | null;
  service?: AppointmentServiceData | null;
  savedLocation?: AppointmentSavedLocationData | null;
  calendar?: AppointmentCalendarData | null;
  enrollment?: AppointmentEnrollmentData | null;
}

export interface CreateAppointmentInput {
  calendarId: string;
  clientId: string;
  dogId?: string;
  appointmentTypeId: string;
  startTime: string;
  endTime: string;
  price?: string;
  notes?: string;
}

export interface UpdateAppointmentInput {
  calendarId?: string;
  clientId?: string;
  dogId?: string | null;
  appointmentTypeId?: string;
  startTime?: string;
  endTime?: string;
  price?: string | null;
  notes?: string | null;
  status?: AppointmentStatus;
  cancellationReason?: string | null;
}

export interface AppointmentListParams {
  page?: number;
  limit?: number;
  status?: AppointmentStatus;
  clientId?: string;
  calendarId?: string;
  startDate?: string;
  endDate?: string;
}

// Availability types
export interface AvailableSlot {
  startTime: string; // ISO datetime
  endTime: string; // ISO datetime
}

// API response wrapper for available slots
interface AvailableSlotsResponse {
  calendarId: string;
  date: string;
  duration: number;
  slots: AvailableSlot[];
}

// Resource types
export interface Resource {
  id: string;
  tenantId: string;
  name: string;
  quantity: number;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateResourceInput {
  name: string;
  quantity?: number;
  description?: string | null;
  isActive?: boolean;
}

export type UpdateResourceInput = Partial<CreateResourceInput>;

// Label types
export interface Label {
  id: string;
  tenantId: string;
  name: string;
  color: string;
  createdAt: string;
}

export interface CreateLabelInput {
  name: string;
  color: string;
}

export type UpdateLabelInput = Partial<CreateLabelInput>;

// Saved Location types
export interface SavedLocation {
  id: string;
  tenantId: string;
  name: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  } | null;
  googleMapsUrl: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}


export interface CreateSavedLocationInput {
  name: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  } | null;
  notes?: string | null;
  isActive?: boolean;
}

export type UpdateSavedLocationInput = Partial<CreateSavedLocationInput>;

// Appointment Addon types
export interface AppointmentAddon {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  price: string;
  duration: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAppointmentAddonInput {
  name: string;
  description?: string | null;
  price: string;
  duration: number;
  isActive?: boolean;
}

export type UpdateAppointmentAddonInput = Partial<CreateAppointmentAddonInput>;

// Class Series types
export interface ClassSeries {
  id: string;
  tenantId: string;
  appointmentTypeId: string;
  calendarId: string;
  name: string;
  description: string | null;
  startDate: string;
  endDate: string | null;
  recurrenceRule: string | null;
  totalSessions: number | null;
  price: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClassSeriesInput {
  appointmentTypeId: string;
  calendarId: string;
  name: string;
  description?: string | null;
  startDate: string;
  endDate?: string | null;
  recurrenceRule?: string | null;
  totalSessions?: number | null;
  price?: string | null;
  isActive?: boolean;
}

export type UpdateClassSeriesInput = Partial<CreateClassSeriesInput>;

// Group Class Instance types
export type GroupClassInstanceStatus =
  | "scheduled"
  | "in_progress"
  | "completed"
  | "cancelled";

export interface GroupClassInstance {
  id: string;
  tenantId: string;
  appointmentTypeId: string;
  classSeriesId: string | null;
  calendarId: string;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  currentEnrollments: number;
  status: GroupClassInstanceStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGroupClassInstanceInput {
  appointmentTypeId: string;
  classSeriesId?: string | null;
  calendarId: string;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  status?: GroupClassInstanceStatus;
  notes?: string | null;
}

export type UpdateGroupClassInstanceInput =
  Partial<CreateGroupClassInstanceInput>;

// Class Enrollment types
export type ClassEnrollmentStatus =
  | "enrolled"
  | "attended"
  | "no_show"
  | "cancelled";

export interface ClassEnrollment {
  id: string;
  tenantId: string;
  classInstanceId: string;
  clientId: string;
  dogId: string | null;
  status: ClassEnrollmentStatus;
  paidAmount: string | null;
  paymentStatus: string | null;
  enrolledAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClassEnrollmentInput {
  classInstanceId: string;
  clientId: string;
  dogId?: string | null;
  status?: ClassEnrollmentStatus;
  paidAmount?: string | null;
  paymentStatus?: string | null;
}

export type UpdateClassEnrollmentInput = Partial<CreateClassEnrollmentInput>;

export interface AttendanceInput {
  status: "attended" | "no_show";
}

// Dog Service Enrollment types
export type DogServiceEnrollmentStatus =
  | "active"
  | "paused"
  | "completed"
  | "cancelled";

export interface DogServiceEnrollment {
  id: string;
  tenantId: string;
  dogId: string;
  appointmentTypeId: string;
  status: DogServiceEnrollmentStatus;
  appointmentsCompleted: number;
  enrolledAt: string;
  completedAt: string | null;
  completedByUserId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDogServiceEnrollmentInput {
  dogId: string;
  appointmentTypeId: string;
  status?: DogServiceEnrollmentStatus;
  notes?: string | null;
}

export type UpdateDogServiceEnrollmentInput = Partial<
  Omit<CreateDogServiceEnrollmentInput, "dogId" | "appointmentTypeId">
>;

// Dog Vaccination types
export interface DogVaccination {
  id: string;
  tenantId: string;
  dogId: string;
  vaccinationType: string;
  administeredDate: string;
  expirationDate: string | null;
  veterinarian: string | null;
  documentUrl: string | null;
  notes: string | null;
  createdAt: string;
}

export interface CreateDogVaccinationInput {
  dogId: string;
  vaccinationType: string;
  administeredDate: string;
  expirationDate?: string | null;
  veterinarian?: string | null;
  documentUrl?: string | null;
  notes?: string | null;
}

export type UpdateDogVaccinationInput = Partial<
  Omit<CreateDogVaccinationInput, "dogId">
>;

// Form types
export interface Form {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  type: string;
  isInternal: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFormInput {
  name: string;
  description?: string | null;
  type: string;
  isInternal?: boolean;
  isActive?: boolean;
}

export type UpdateFormInput = Partial<CreateFormInput>;

// Form Field types
export interface FormField {
  id: string;
  tenantId: string;
  formId: string;
  fieldType: string;
  label: string;
  placeholder: string | null;
  options: string[] | null;
  isRequired: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface CreateFormFieldInput {
  formId: string;
  fieldType: string;
  label: string;
  placeholder?: string | null;
  options?: string[] | null;
  isRequired?: boolean;
  sortOrder?: number;
}

export type UpdateFormFieldInput = Partial<
  Omit<CreateFormFieldInput, "formId">
>;

// Form Submission types
export interface FormSubmission {
  id: string;
  tenantId: string;
  formId: string;
  appointmentId: string | null;
  clientId: string | null;
  dogId: string | null;
  responses: Record<string, unknown>;
  submittedAt: string;
}

export interface CreateFormSubmissionInput {
  formId: string;
  appointmentId?: string | null;
  clientId?: string | null;
  dogId?: string | null;
  responses: Record<string, unknown>;
}

export type UpdateFormSubmissionInput = Partial<CreateFormSubmissionInput>;

// Availability Rule types
export interface AvailabilityRule {
  id: string;
  tenantId: string;
  calendarId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAvailabilityRuleInput {
  calendarId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive?: boolean;
}

export type UpdateAvailabilityRuleInput = Partial<
  Omit<CreateAvailabilityRuleInput, "calendarId">
>;

// Availability Override types
export interface AvailabilityOverride {
  id: string;
  tenantId: string;
  calendarId: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  isAvailable: boolean;
  reason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAvailabilityOverrideInput {
  calendarId: string;
  date: string;
  startTime?: string | null;
  endTime?: string | null;
  isAvailable: boolean;
  reason?: string | null;
}

export type UpdateAvailabilityOverrideInput = Partial<
  Omit<CreateAvailabilityOverrideInput, "calendarId">
>;

// Blocked Time types
export interface BlockedTime {
  id: string;
  tenantId: string;
  calendarId: string;
  startTime: string;
  endTime: string;
  reason: string | null;
  isRecurring: boolean;
  recurrenceRule: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBlockedTimeInput {
  calendarId: string;
  startTime: string;
  endTime: string;
  reason?: string | null;
  isRecurring?: boolean;
  recurrenceRule?: string | null;
}

export type UpdateBlockedTimeInput = Partial<
  Omit<CreateBlockedTimeInput, "calendarId">
>;

export const api = {
  auth: {
    login: (email: string, password: string) =>
      request<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }),
    register: (
      email: string,
      password: string,
      name: string,
      tenantName: string,
    ) =>
      request<AuthResponse>("/auth/register", {
        method: "POST",
        body: JSON.stringify({ email, password, name, tenantName }),
      }),
    forgotPassword: (email: string) =>
      request<{ message: string }>("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    resetPassword: (token: string, password: string) =>
      request<{ message: string }>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      }),
  },
  me: {
    get: () => request<MeResponse>("/me"),
  },
  clients: {
    list: (params?: { page?: number; limit?: number; search?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      if (params?.search) searchParams.set("search", params.search);
      const query = searchParams.toString();
      return request<PaginatedResponse<Client>>(
        `/clients${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<Client>(`/clients/${id}`),
    create: (data: CreateClientInput) =>
      request<Client>("/clients", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateClientInput) =>
      request<Client>(`/clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/clients/${id}`, {
        method: "DELETE",
      }),
  },
  dogs: {
    byClient: (clientId: string) =>
      request<PaginatedResponse<Dog>>(`/dogs/by-client/${clientId}`),
    get: (id: string) => request<Dog>(`/dogs/${id}`),
    create: (data: CreateDogInput) =>
      request<Dog>("/dogs", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateDogInput) =>
      request<Dog>(`/dogs/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/dogs/${id}`, {
        method: "DELETE",
      }),
  },
  calendars: {
    list: (params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      const query = searchParams.toString();
      return request<PaginatedResponse<Calendar>>(
        `/calendars${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<Calendar>(`/calendars/${id}`),
    create: (data: CreateCalendarInput) =>
      request<Calendar>("/calendars", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateCalendarInput) =>
      request<Calendar>(`/calendars/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/calendars/${id}`, {
        method: "DELETE",
      }),
  },
  services: {
    list: (params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      const query = searchParams.toString();
      return request<PaginatedResponse<Service>>(
        `/services${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<Service>(`/services/${id}`),
    create: (data: CreateServiceInput) =>
      request<Service>("/services", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateServiceInput) =>
      request<Service>(`/services/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/services/${id}`, {
        method: "DELETE",
      }),
  },
  appointments: {
    list: (params?: AppointmentListParams) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      if (params?.status) searchParams.set("status", params.status);
      if (params?.clientId) searchParams.set("clientId", params.clientId);
      if (params?.calendarId) searchParams.set("calendarId", params.calendarId);
      if (params?.startDate) searchParams.set("startDate", params.startDate);
      if (params?.endDate) searchParams.set("endDate", params.endDate);
      const query = searchParams.toString();
      return request<PaginatedResponse<Appointment>>(
        `/appointments${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<Appointment>(`/appointments/${id}`),
    create: (data: CreateAppointmentInput) =>
      request<Appointment>("/appointments", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateAppointmentInput) =>
      request<Appointment>(`/appointments/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/appointments/${id}`, {
        method: "DELETE",
      }),
  },
  availability: {
    getSlots: (calendarId: string, date: string, duration: number) =>
      request<AvailableSlotsResponse>(
        `/calendars/${calendarId}/available-slots?date=${date}&duration=${duration}`,
      ).then((res) => res.slots),
    rules: {
      list: (params?: {
        calendarId?: string;
        page?: number;
        limit?: number;
      }) => {
        const searchParams = new URLSearchParams();
        if (params?.calendarId)
          searchParams.set("calendarId", params.calendarId);
        if (params?.page !== undefined)
          searchParams.set("page", String(params.page));
        if (params?.limit !== undefined)
          searchParams.set("limit", String(params.limit));
        const query = searchParams.toString();
        return request<PaginatedResponse<AvailabilityRule>>(
          `/availability-rules${query ? `?${query}` : ""}`,
        );
      },
      get: (id: string) =>
        request<AvailabilityRule>(`/availability-rules/${id}`),
      create: (data: CreateAvailabilityRuleInput) =>
        request<AvailabilityRule>("/availability-rules", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: string, data: UpdateAvailabilityRuleInput) =>
        request<AvailabilityRule>(`/availability-rules/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      delete: (id: string) =>
        request<{ message: string }>(`/availability-rules/${id}`, {
          method: "DELETE",
        }),
    },
    overrides: {
      list: (params?: {
        calendarId?: string;
        page?: number;
        limit?: number;
      }) => {
        const searchParams = new URLSearchParams();
        if (params?.calendarId)
          searchParams.set("calendarId", params.calendarId);
        if (params?.page !== undefined)
          searchParams.set("page", String(params.page));
        if (params?.limit !== undefined)
          searchParams.set("limit", String(params.limit));
        const query = searchParams.toString();
        return request<PaginatedResponse<AvailabilityOverride>>(
          `/availability-overrides${query ? `?${query}` : ""}`,
        );
      },
      get: (id: string) =>
        request<AvailabilityOverride>(`/availability-overrides/${id}`),
      create: (data: CreateAvailabilityOverrideInput) =>
        request<AvailabilityOverride>("/availability-overrides", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: string, data: UpdateAvailabilityOverrideInput) =>
        request<AvailabilityOverride>(`/availability-overrides/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      delete: (id: string) =>
        request<{ message: string }>(`/availability-overrides/${id}`, {
          method: "DELETE",
        }),
    },
    blockedTimes: {
      list: (params?: {
        calendarId?: string;
        page?: number;
        limit?: number;
      }) => {
        const searchParams = new URLSearchParams();
        if (params?.calendarId)
          searchParams.set("calendarId", params.calendarId);
        if (params?.page !== undefined)
          searchParams.set("page", String(params.page));
        if (params?.limit !== undefined)
          searchParams.set("limit", String(params.limit));
        const query = searchParams.toString();
        return request<PaginatedResponse<BlockedTime>>(
          `/blocked-times${query ? `?${query}` : ""}`,
        );
      },
      get: (id: string) => request<BlockedTime>(`/blocked-times/${id}`),
      create: (data: CreateBlockedTimeInput) =>
        request<BlockedTime>("/blocked-times", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: string, data: UpdateBlockedTimeInput) =>
        request<BlockedTime>(`/blocked-times/${id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
      delete: (id: string) =>
        request<{ message: string }>(`/blocked-times/${id}`, {
          method: "DELETE",
        }),
    },
  },
  resources: {
    list: (params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      const query = searchParams.toString();
      return request<PaginatedResponse<Resource>>(
        `/resources${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<Resource>(`/resources/${id}`),
    create: (data: CreateResourceInput) =>
      request<Resource>("/resources", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateResourceInput) =>
      request<Resource>(`/resources/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/resources/${id}`, {
        method: "DELETE",
      }),
  },
  labels: {
    list: (params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      const query = searchParams.toString();
      return request<PaginatedResponse<Label>>(
        `/labels${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<Label>(`/labels/${id}`),
    create: (data: CreateLabelInput) =>
      request<Label>("/labels", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateLabelInput) =>
      request<Label>(`/labels/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/labels/${id}`, {
        method: "DELETE",
      }),
  },
  savedLocations: {
    list: (params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      const query = searchParams.toString();
      return request<PaginatedResponse<SavedLocation>>(
        `/saved-locations${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<SavedLocation>(`/saved-locations/${id}`),
    create: (data: CreateSavedLocationInput) =>
      request<SavedLocation>("/saved-locations", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateSavedLocationInput) =>
      request<SavedLocation>(`/saved-locations/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/saved-locations/${id}`, {
        method: "DELETE",
      }),
  },
  appointmentAddons: {
    list: (params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      const query = searchParams.toString();
      return request<PaginatedResponse<AppointmentAddon>>(
        `/appointment-addons${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<AppointmentAddon>(`/appointment-addons/${id}`),
    create: (data: CreateAppointmentAddonInput) =>
      request<AppointmentAddon>("/appointment-addons", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateAppointmentAddonInput) =>
      request<AppointmentAddon>(`/appointment-addons/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/appointment-addons/${id}`, {
        method: "DELETE",
      }),
  },
  classSeries: {
    list: (params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      const query = searchParams.toString();
      return request<PaginatedResponse<ClassSeries>>(
        `/class-series${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<ClassSeries>(`/class-series/${id}`),
    create: (data: CreateClassSeriesInput) =>
      request<ClassSeries>("/class-series", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateClassSeriesInput) =>
      request<ClassSeries>(`/class-series/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/class-series/${id}`, {
        method: "DELETE",
      }),
  },
  groupClassInstances: {
    list: (params?: {
      page?: number;
      limit?: number;
      classSeriesId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      if (params?.classSeriesId)
        searchParams.set("classSeriesId", params.classSeriesId);
      const query = searchParams.toString();
      return request<PaginatedResponse<GroupClassInstance>>(
        `/group-class-instances${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) =>
      request<GroupClassInstance>(`/group-class-instances/${id}`),
    create: (data: CreateGroupClassInstanceInput) =>
      request<GroupClassInstance>("/group-class-instances", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateGroupClassInstanceInput) =>
      request<GroupClassInstance>(`/group-class-instances/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/group-class-instances/${id}`, {
        method: "DELETE",
      }),
  },
  classEnrollments: {
    list: (params?: {
      page?: number;
      limit?: number;
      classInstanceId?: string;
      clientId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      if (params?.classInstanceId)
        searchParams.set("classInstanceId", params.classInstanceId);
      if (params?.clientId) searchParams.set("clientId", params.clientId);
      const query = searchParams.toString();
      return request<PaginatedResponse<ClassEnrollment>>(
        `/class-enrollments${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<ClassEnrollment>(`/class-enrollments/${id}`),
    create: (data: CreateClassEnrollmentInput) =>
      request<ClassEnrollment>("/class-enrollments", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateClassEnrollmentInput) =>
      request<ClassEnrollment>(`/class-enrollments/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/class-enrollments/${id}`, {
        method: "DELETE",
      }),
    markAttendance: (id: string, data: AttendanceInput) =>
      request<ClassEnrollment>(`/class-enrollments/${id}/attendance`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
  dogServiceEnrollments: {
    list: (params?: {
      page?: number;
      limit?: number;
      dogId?: string;
      status?: DogServiceEnrollmentStatus;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      if (params?.dogId) searchParams.set("dogId", params.dogId);
      if (params?.status) searchParams.set("status", params.status);
      const query = searchParams.toString();
      return request<PaginatedResponse<DogServiceEnrollment>>(
        `/dog-service-enrollments${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) =>
      request<DogServiceEnrollment>(`/dog-service-enrollments/${id}`),
    create: (data: CreateDogServiceEnrollmentInput) =>
      request<DogServiceEnrollment>("/dog-service-enrollments", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateDogServiceEnrollmentInput) =>
      request<DogServiceEnrollment>(`/dog-service-enrollments/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/dog-service-enrollments/${id}`, {
        method: "DELETE",
      }),
    increment: (id: string) =>
      request<DogServiceEnrollment>(
        `/dog-service-enrollments/${id}/increment`,
        {
          method: "POST",
        },
      ),
    complete: (id: string) =>
      request<DogServiceEnrollment>(`/dog-service-enrollments/${id}/complete`, {
        method: "POST",
      }),
  },
  vaccinations: {
    list: (params?: { page?: number; limit?: number; dogId?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      if (params?.dogId) searchParams.set("dogId", params.dogId);
      const query = searchParams.toString();
      return request<PaginatedResponse<DogVaccination>>(
        `/vaccinations${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<DogVaccination>(`/vaccinations/${id}`),
    create: (data: CreateDogVaccinationInput) =>
      request<DogVaccination>("/vaccinations", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateDogVaccinationInput) =>
      request<DogVaccination>(`/vaccinations/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/vaccinations/${id}`, {
        method: "DELETE",
      }),
  },
  forms: {
    list: (params?: { page?: number; limit?: number; type?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      if (params?.type) searchParams.set("type", params.type);
      const query = searchParams.toString();
      return request<PaginatedResponse<Form>>(
        `/forms${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<Form>(`/forms/${id}`),
    create: (data: CreateFormInput) =>
      request<Form>("/forms", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateFormInput) =>
      request<Form>(`/forms/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/forms/${id}`, {
        method: "DELETE",
      }),
  },
  formFields: {
    list: (formId: string, params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      searchParams.set("formId", formId);
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      const query = searchParams.toString();
      return request<PaginatedResponse<FormField>>(`/form-fields?${query}`);
    },
    get: (id: string) => request<FormField>(`/form-fields/${id}`),
    create: (data: CreateFormFieldInput) =>
      request<FormField>("/form-fields", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateFormFieldInput) =>
      request<FormField>(`/form-fields/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/form-fields/${id}`, {
        method: "DELETE",
      }),
  },
  formSubmissions: {
    list: (params?: {
      page?: number;
      limit?: number;
      formId?: string;
      clientId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined)
        searchParams.set("page", String(params.page));
      if (params?.limit !== undefined)
        searchParams.set("limit", String(params.limit));
      if (params?.formId) searchParams.set("formId", params.formId);
      if (params?.clientId) searchParams.set("clientId", params.clientId);
      const query = searchParams.toString();
      return request<PaginatedResponse<FormSubmission>>(
        `/form-submissions${query ? `?${query}` : ""}`,
      );
    },
    get: (id: string) => request<FormSubmission>(`/form-submissions/${id}`),
    create: (data: CreateFormSubmissionInput) =>
      request<FormSubmission>("/form-submissions", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateFormSubmissionInput) =>
      request<FormSubmission>(`/form-submissions/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ message: string }>(`/form-submissions/${id}`, {
        method: "DELETE",
      }),
  },
};
